import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { getServerUrl } from '../utils/serverUrl';
import { setItem as lsSet, removeItem as lsRemove } from '../utils/storage';
import {
  generateKyberKeypair,
  generateDilithiumKeypair,
  signBundle,
  hybridWrap,
  hybridUnwrap,
  b64,
} from '../utils/pqCrypto';
import {
  shouldRotate,
  rotateKeys,
  loadKeyRing,
  importRsaPrivate,
} from '../utils/keyRotation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ghostcomm_token'));
  const [loading, setLoading] = useState(true);
  const [cryptoKeys, setCryptoKeys] = useState(null);
  const wsRef = useRef(null);
  const wsListenersRef = useRef([]);

  // Initialize axios interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  // Generate or load encryption keys (classical RSA + PQ Kyber/Dilithium)
  const initCryptoKeys = useCallback(async () => {
    // ── Classical RSA (existing) ──────────────────────────────────
    const storedKeys = localStorage.getItem('ghostcomm_keys');
    let privateKey, publicKey, publicKeyJwk;
    if (storedKeys) {
      const keys = JSON.parse(storedKeys);
      privateKey = await window.crypto.subtle.importKey(
        'jwk', keys.privateKey, { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['decrypt']
      );
      publicKey = await window.crypto.subtle.importKey(
        'jwk', keys.publicKey, { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['encrypt']
      );
      publicKeyJwk = keys.publicKey;
    } else {
      const keyPair = await window.crypto.subtle.generateKey(
        {
          name: 'RSA-OAEP',
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: 'SHA-256',
        },
        true,
        ['encrypt', 'decrypt']
      );
      publicKeyJwk = await window.crypto.subtle.exportKey('jwk', keyPair.publicKey);
      const privateKeyJwk = await window.crypto.subtle.exportKey('jwk', keyPair.privateKey);
      lsSet('ghostcomm_keys', JSON.stringify({
        publicKey: publicKeyJwk,
        privateKey: privateKeyJwk,
      }));
      privateKey = keyPair.privateKey;
      publicKey = keyPair.publicKey;
    }

    // ── Post-Quantum (ML-KEM-768 + ML-DSA-65) ─────────────────────
    // Keys are generated once per device and cached. Regenerating
    // would invalidate historical ciphertext — never do it except
    // via an explicit rotation flow (Week 5 work).
    let pqStored = localStorage.getItem('ghostcomm_pq_keys');
    let kyberPub, kyberSecret, dilithiumPub, dilithiumSecret;
    if (pqStored) {
      try {
        const parsed = JSON.parse(pqStored);
        kyberPub = b64.decode(parsed.kyber_pub);
        kyberSecret = b64.decode(parsed.kyber_secret);
        dilithiumPub = b64.decode(parsed.dilithium_pub);
        dilithiumSecret = b64.decode(parsed.dilithium_secret);
      } catch (_e) { pqStored = null; }
    }
    if (!pqStored) {
      const kem = generateKyberKeypair();
      const dsa = generateDilithiumKeypair();
      kyberPub = kem.publicKey;
      kyberSecret = kem.secretKey;
      dilithiumPub = dsa.publicKey;
      dilithiumSecret = dsa.secretKey;
      lsSet('ghostcomm_pq_keys', JSON.stringify({
        kyber_pub: b64.encode(kyberPub),
        kyber_secret: b64.encode(kyberSecret),
        dilithium_pub: b64.encode(dilithiumPub),
        dilithium_secret: b64.encode(dilithiumSecret),
      }));
    }

    setCryptoKeys({
      privateKey,
      publicKey,
      publicKeyJwk,
      kyberPub,
      kyberSecret,
      dilithiumPub,
      dilithiumSecret,
    });

    return publicKeyJwk;
  }, []);

  // WebSocket connection
  const connectWebSocket = useCallback((authToken) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = getServerUrl().replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/api/ws/${authToken}`);

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      wsListenersRef.current.forEach(listener => listener(data));
    };

    wsRef.current.onclose = () => {
      setTimeout(() => {
        if (token) connectWebSocket(authToken);
      }, 3000);
    };
  }, [token]);

  const addWsListener = useCallback((listener) => {
    wsListenersRef.current.push(listener);
    return () => {
      wsListenersRef.current = wsListenersRef.current.filter(l => l !== listener);
    };
  }, []);

  const sendWsMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // ─── Post-quantum bundle upload ─────────────────────────────────
  // When both the user session and the PQ keypairs are ready, upload
  // (or refresh) the signed public-key bundle so peers can fetch it
  // for hybrid encryption. Idempotent — safe to run on every session
  // init; the server just replaces. Tracked by a single "uploaded"
  // ref so we don't DoS the endpoint on re-renders.
  const pqBundleUploadedRef = useRef(false);
  useEffect(() => {
    if (!user?.id || !cryptoKeys?.dilithiumSecret || !cryptoKeys?.kyberPub) return;
    if (pqBundleUploadedRef.current) return;
    (async () => {
      try {
        const createdAt = new Date().toISOString();
        const { kyber_pub_b64, bundle_sig_b64 } = signBundle(
          cryptoKeys.publicKeyJwk,
          cryptoKeys.kyberPub,
          user.id,
          cryptoKeys.dilithiumSecret,
          createdAt,
        );
        await axios.put(`${API}/auth/pq-bundle`, {
          kyber_pub: kyber_pub_b64,
          dilithium_pub: b64.encode(cryptoKeys.dilithiumPub),
          bundle_sig: bundle_sig_b64,
          created_at: createdAt,
        });
        pqBundleUploadedRef.current = true;
      } catch (_e) {
        // Non-fatal — classical path still works. Retry on next session.
      }
    })();
  }, [user?.id, cryptoKeys]);

  // ─── Automatic 7-day key rotation ───────────────────────────────
  // Runs opportunistically once per session when both the user and
  // the PQ identity key are loaded AND >7 days have elapsed since
  // the last rotation. Idempotent within a session via a ref guard
  // so repeated re-renders don't re-rotate. On success the fresh
  // key material replaces `cryptoKeys`; on failure we silently
  // leave the previous keys in place and try again on next login
  // (the rotation-timestamp stamp is only written on full success).
  const rotationCheckedRef = useRef(false);
  useEffect(() => {
    if (rotationCheckedRef.current) return;
    if (!user?.id || !cryptoKeys?.dilithiumSecret) return;
    // Never rotate in duress — the decoy session has no server-side
    // keys to rotate and hitting the real endpoints would leak.
    if (typeof window !== 'undefined' &&
        sessionStorage.getItem('gs_duress_mode') === '1') {
      rotationCheckedRef.current = true;
      return;
    }
    if (!shouldRotate()) {
      rotationCheckedRef.current = true;
      return;
    }
    rotationCheckedRef.current = true;
    (async () => {
      try {
        const fresh = await rotateKeys({
          user,
          dilithiumPub: cryptoKeys.dilithiumPub,
          dilithiumSecret: cryptoKeys.dilithiumSecret,
        });
        // Keep the Dilithium identity bytes in the new state — they
        // didn't rotate and the fresh result only carries the pieces
        // that DID rotate.
        setCryptoKeys((prev) => ({
          ...prev,
          publicKey: fresh.publicKey,
          privateKey: fresh.privateKey,
          publicKeyJwk: fresh.publicKeyJwk,
          kyberPub: fresh.kyberPub,
          kyberSecret: fresh.kyberSecret,
        }));
        // Force the next PQ-bundle effect to re-upload — it was
        // already done with the OLD Kyber pubkey during initSession.
        pqBundleUploadedRef.current = true; // already uploaded inside rotateKeys
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[ghostcomm] key rotation failed — will retry next session:', e?.message || e);
      }
    })();
  }, [user?.id, cryptoKeys?.dilithiumSecret, cryptoKeys?.dilithiumPub]);

  // Manual trigger — exposed on the context so the Settings panel
  // can offer a "Rotate now" button. Resets the session guard so a
  // subsequent failed + retried click still attempts.
  const rotateKeysNow = useCallback(async () => {
    // Defense-in-depth: never rotate under a duress session even if
    // the decoy UI somehow surfaces the Settings → Privacy button.
    // The automatic effect already bails on this; mirror the check
    // on the manual path so the two surfaces can't diverge.
    if (typeof window !== 'undefined' &&
        sessionStorage.getItem('gs_duress_mode') === '1') {
      throw new Error('Not available in duress mode');
    }
    if (!user?.id || !cryptoKeys?.dilithiumSecret) {
      throw new Error('Keys not ready yet');
    }
    const fresh = await rotateKeys({
      user,
      dilithiumPub: cryptoKeys.dilithiumPub,
      dilithiumSecret: cryptoKeys.dilithiumSecret,
    });
    setCryptoKeys((prev) => ({
      ...prev,
      publicKey: fresh.publicKey,
      privateKey: fresh.privateKey,
      publicKeyJwk: fresh.publicKeyJwk,
      kyberPub: fresh.kyberPub,
      kyberSecret: fresh.kyberSecret,
    }));
    return fresh;
  }, [user, cryptoKeys?.dilithiumSecret, cryptoKeys?.dilithiumPub]);

  // Initialize user session
  useEffect(() => {
    const initSession = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      // Duress: do NOT probe /auth/me and do NOT PUT /auth/public-key.
      // Those calls would reveal the authenticated session to the
      // server (and any coerced observer) and can overwrite the real
      // account's public key. The duress login path has already
      // populated `user` from the /auth/login response and pivoted
      // the UI to the decoy vault. Read from sessionStorage rather
      // than state because `setToken` fires this effect before the
      // `isDuressMode` state update from login() has landed.
      if (typeof window !== 'undefined' &&
          sessionStorage.getItem('gs_duress_mode') === '1') {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        Sentry.setUser({ id: response.data?.id });

        const publicKeyJwk = await initCryptoKeys();

        // Update public key on server if needed
        if (!response.data.public_key || JSON.stringify(response.data.public_key) !== JSON.stringify(publicKeyJwk)) {
          await axios.put(`${API}/auth/public-key`, {
            public_key: JSON.stringify(publicKeyJwk),
          });
        }

        connectWebSocket(token);
      } catch (error) {
        console.error('Session init error:', error);
        lsRemove('ghostcomm_token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    initSession();
  }, [token, initCryptoKeys, connectWebSocket]);

  // Duress mode — when true, the UI swaps to the client-side decoy
  // vault. Every read path checks this flag and serves the decoy
  // rather than hitting the real API. Persisted in sessionStorage so
  // a refresh inside a duress session doesn't accidentally fall
  // back to the primary account.
  const [isDuressMode, setIsDuressMode] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem('gs_duress_mode') === '1',
  );
  const [duressState, setDuressState] = useState(null);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, duress } = response.data;
    lsSet('ghostcomm_token', access_token);
    setToken(access_token);
    setUser(userData);
    // Sentry user context — id only, never email/username (PII).
    Sentry.setUser({ id: userData?.id });
    if (duress) {
      // Duress login — open the client-side decoy vault, pivot the
      // UI, and DO NOT initialise real crypto keys. Every read path
      // respects `isDuressMode` and serves decoy data.
      const { tryOpenDuressVault } = await import('../utils/duressVault');
      const decoy = await tryOpenDuressVault(password);
      if (decoy) {
        setDuressState(decoy);
        setIsDuressMode(true);
        sessionStorage.setItem('gs_duress_mode', '1');
        // Deliberately skip initCryptoKeys + WS connect — nothing
        // real should load. The decoy already has everything the
        // Dashboard needs to render a plausible session.
        return userData;
      }
      // Server said duress=true but our local vault is missing
      // (fresh device). For v1 we fall through to normal behaviour
      // so the user isn't locked out — they just won't see decoy
      // data. The silent alert already fired server-side.
    }
    await initCryptoKeys();
    connectWebSocket(access_token);
    return userData;
  };

  const verifyEmail = async (email, token) => {
    const { data } = await axios.post(`${API}/auth/verify-email`, { email, token });
    return data;
  };

  const resendVerification = async (email) => {
    // Always resolves — the backend is enumeration-safe and returns
    // 200 even for unknown emails / already-verified users. Rate
    // limiting surfaces as HTTP 429, which we let bubble up.
    const { data } = await axios.post(`${API}/auth/resend-verification`, { email });
    return data;
  };

  const register = async (username, email, password, inviteCode) => {
    const payload = { username, email, password };
    if (inviteCode) payload.invite_code = inviteCode;
    const response = await axios.post(`${API}/auth/register`, payload);
    const { access_token, user: userData, recovery_key } = response.data;
    // DO NOT activate session yet — caller must show the recovery key first.
    // After acknowledgement, caller calls activateSessionFromRegister(accessToken, user).
    return { user: userData, recoveryKey: recovery_key, accessToken: access_token };
  };

  const activateSessionFromRegister = async (accessToken, userData) => {
    localStorage.setItem('ghostcomm_token', accessToken);
    setToken(accessToken);
    setUser(userData);
    const publicKeyJwk = await initCryptoKeys();
    // Public key upload must happen WITH the Bearer token that's now in localStorage
    // (axios auto-attaches from the interceptor set up in setupAxios). We pass it
    // explicitly to avoid any race with the token-setter.
    await axios.put(`${API}/auth/public-key`,
      { public_key: JSON.stringify(publicKeyJwk) },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    connectWebSocket(accessToken);
  };

  const resetPassword = async (email, recoveryKey, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      email,
      recovery_key: recoveryKey,
      new_password: newPassword,
    });
    const { access_token, user: userData, recovery_key: newRecoveryKey } = response.data;
    // DO NOT activate the session yet — caller must show the new recovery key first.
    // Caller calls `activateSession(accessToken, user)` after the user acknowledges.
    return { user: userData, recoveryKey: newRecoveryKey, accessToken: access_token };
  };

  const activateSession = async (accessToken, userData) => {
    lsSet('ghostcomm_token', accessToken);
    setToken(accessToken);
    setUser(userData);
    // Client-side RSA keys in localStorage stay intact on THIS device, so message history
    // is still decryptable. A fresh device regenerates keys and loses encrypted history
    // (same behaviour as Signal).
    await initCryptoKeys();
    connectWebSocket(accessToken);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Network / already-expired-token — local cleanup below still
      // happens. Logging out is idempotent from the user's perspective.
      if (process.env.NODE_ENV !== 'production') console.debug('logout API failed:', e);
    }
    lsRemove('ghostcomm_token');
    wsRef.current?.close();
    setToken(null);
    setUser(null);
    // Clear any duress-session state but DO NOT delete the duress
    // vault itself — removing the vault has its own endpoint so
    // logout-then-login still works in duress mode.
    sessionStorage.removeItem('gs_duress_mode');
    setIsDuressMode(false);
    setDuressState(null);
    Sentry.setUser(null);
  };

  // Encryption helpers
  // encryptMessage now accepts an OPTIONAL `recipientPqBundles` map
  // of the shape {user_id: {kyber_pub_b64, dilithium_pub_b64, ...}}.
  // For recipients with a Kyber pubkey, we produce a hybrid envelope
  // stored under `encrypted_keys_pq[user_id]`; for everyone else the
  // classical RSA path still applies. The two layers coexist so
  // mixed-mode groups (one legacy, one PQ) still work.
  const encryptMessage = async (message, recipientPublicKeys, recipientPqBundles = {}) => {
    // Generate a random AES key for this message
    const aesKey = await window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );

    // Encrypt the message with AES
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encoder = new TextEncoder();
    const encryptedContent = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      encoder.encode(message)
    );

    // Export AES key
    const rawAesKey = await window.crypto.subtle.exportKey('raw', aesKey);

    // Encrypt AES key for each recipient using their public key
    const encryptedKeys = {};
    const encryptedKeysPq = {};
    for (const [userId, publicKeyJwk] of Object.entries(recipientPublicKeys)) {
      // Classical wrap — always present for backward compat.
      const publicKey = await window.crypto.subtle.importKey(
        'jwk',
        JSON.parse(publicKeyJwk),
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt']
      );
      const encryptedAesKey = await window.crypto.subtle.encrypt(
        { name: 'RSA-OAEP' },
        publicKey,
        rawAesKey
      );
      encryptedKeys[userId] = btoa(String.fromCharCode(...new Uint8Array(encryptedAesKey)));

      // PQ hybrid wrap — only for recipients whose bundle we have
      // AND whose Kyber pubkey looks valid (1184 bytes raw).
      const bundle = recipientPqBundles?.[userId];
      if (bundle?.kyber_pub_b64) {
        try {
          const kyberPub = b64.decode(bundle.kyber_pub_b64);
          if (kyberPub.length === 1184) {
            encryptedKeysPq[userId] = await hybridWrap(
              new Uint8Array(rawAesKey),
              JSON.parse(publicKeyJwk),
              kyberPub
            );
          }
        } catch (_e) {
          // Non-fatal — recipient can still decrypt via RSA fallback.
        }
      }
    }

    // Also encrypt for self (classical + PQ if we have our own keys)
    if (cryptoKeys?.publicKey) {
      const encryptedAesKey = await window.crypto.subtle.encrypt(
        { name: 'RSA-OAEP' },
        cryptoKeys.publicKey,
        rawAesKey
      );
      encryptedKeys[user.id] = btoa(String.fromCharCode(...new Uint8Array(encryptedAesKey)));
      if (cryptoKeys?.kyberPub) {
        try {
          encryptedKeysPq[user.id] = await hybridWrap(
            new Uint8Array(rawAesKey),
            cryptoKeys.publicKeyJwk,
            cryptoKeys.kyberPub
          );
        } catch (_e) { /* classical path still works */ }
      }
    }

    return {
      encrypted_content: JSON.stringify({
        iv: btoa(String.fromCharCode(...iv)),
        data: btoa(String.fromCharCode(...new Uint8Array(encryptedContent))),
      }),
      encrypted_keys: encryptedKeys,
      encrypted_keys_pq: Object.keys(encryptedKeysPq).length > 0 ? encryptedKeysPq : undefined,
    };
  };

  // Dedupe log-spam: each unique decryption-error signature is only logged once per session.
  const loggedDecryptErrorsRef = useRef(new Set());

  // decryptMessage: hybrid-aware + archive-aware. If a hybrid envelope
  // is provided, we recover the AES key via RSA+Kyber combine; otherwise
  // we fall back to the classical RSA-only path. If BOTH fail against
  // the current keypair, we walk the archive ring — this lets messages
  // encrypted against a previously-rotated key still decrypt on the
  // same device. (Historical ciphertext is preserved locally; the
  // archive never travels to the server.)
  const decryptMessage = async (encryptedContent, encryptedKey, pqEnvelope = null) => {
    if (!cryptoKeys?.privateKey) return '[Encrypted for a different device]';
    if (!encryptedKey && !pqEnvelope) return '[Encrypted for a different device]';

    // Try the current keypair first, then walk the archive ring on
    // OperationError (the WebCrypto signal for "wrong private key").
    const tryOne = async ({ privateKey, kyberSecret }) => {
      let rawAesKey;
      if (pqEnvelope && kyberSecret) {
        rawAesKey = await hybridUnwrap(pqEnvelope, privateKey, kyberSecret);
      } else {
        if (!encryptedKey) throw new Error('No classical wrap');
        if (!/^[A-Za-z0-9+/]+=*$/.test(encryptedKey)) {
          throw new DOMException('bad base64', 'InvalidCharacterError');
        }
        const encryptedAesKeyBuffer = Uint8Array.from(
          atob(encryptedKey), (c) => c.charCodeAt(0)
        );
        rawAesKey = await window.crypto.subtle.decrypt(
          { name: 'RSA-OAEP' }, privateKey, encryptedAesKeyBuffer
        );
      }
      const aesKey = await window.crypto.subtle.importKey(
        'raw', rawAesKey, { name: 'AES-GCM', length: 256 }, false, ['decrypt']
      );
      const { iv, data } = JSON.parse(encryptedContent);
      const ivBuffer = Uint8Array.from(atob(iv), (c) => c.charCodeAt(0));
      const dataBuffer = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: ivBuffer }, aesKey, dataBuffer
      );
      return new TextDecoder().decode(decrypted);
    };

    // Build the candidate list: current keys first, then each
    // archive entry (newest archive entry before older ones).
    const buildArchiveCandidates = async () => {
      const ring = await loadKeyRing();
      const candidates = [];
      for (const entry of ring) {
        if (entry.current) continue; // already tried above
        try {
          const priv = await importRsaPrivate(entry.classical?.privateKey);
          let kyberSecret = null;
          if (entry.pq?.kyber_secret) {
            try { kyberSecret = b64.decode(entry.pq.kyber_secret); }
            catch { kyberSecret = null; }
          }
          if (priv) candidates.push({ privateKey: priv, kyberSecret });
        } catch { /* skip malformed archive entry */ }
      }
      return candidates;
    };

    try {
      return await tryOne({
        privateKey: cryptoKeys.privateKey,
        kyberSecret: cryptoKeys.kyberSecret,
      });
    } catch (error) {
      // Archive fallback is only useful when the failure is "wrong
      // key". Other errors (bad base64, corrupt JSON) won't be cured
      // by trying a different private key — skip the walk.
      const name = error?.name || '';
      if (name === 'OperationError') {
        try {
          const archives = await buildArchiveCandidates();
          for (const a of archives) {
            try {
              return await tryOne(a);
            } catch { /* try next archived key */ }
          }
        } catch { /* archive inaccessible — fall through to original error handling */ }
      }

      // Classify: expected failures (legacy messages, cross-device) vs unexpected bugs.
      const expected = (
        name === 'OperationError' ||          // WebCrypto: wrong key
        name === 'InvalidCharacterError' ||   // atob: malformed base64 in content
        name === 'SyntaxError'                // JSON.parse: malformed content payload
      );
      const sig = `${name}:${(error?.message || '').slice(0, 80)}`;
      if (expected) {
        if (!loggedDecryptErrorsRef.current.has(sig)) {
          loggedDecryptErrorsRef.current.add(sig);
          // eslint-disable-next-line no-console
          console.debug('[ghostcomm] skipping undecryptable message:', sig);
        }
        return '[Encrypted for a different device]';
      }
      if (!loggedDecryptErrorsRef.current.has(sig)) {
        loggedDecryptErrorsRef.current.add(sig);
        // eslint-disable-next-line no-console
        console.warn('[ghostcomm] unexpected decryption error:', error);
      }
      return '[Decryption failed]';
    }
  };

  // Re-fetch /auth/me and update the cached user object. Used by
  // surfaces that mutate user state via dedicated endpoints (e.g.
  // PUT /auth/me/default-ttl) so the rest of the app sees the
  // refreshed value without a full page reload.
  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      resetPassword,
      activateSession,
      activateSessionFromRegister,
      verifyEmail,
      resendVerification,
      logout,
      cryptoKeys,
      encryptMessage,
      decryptMessage,
      addWsListener,
      sendWsMessage,
      isDuressMode,
      duressState,
      setDuressState,
      rotateKeysNow,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
